import React, { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Search, X } from 'lucide-react';

// Helper function to get default avatar based on gender
const getDefaultAvatar = (gender) => {
  // Handle different gender formats from backend
  if (gender === 'female' || gender === 'Mujer' || gender === 'F' || gender === 'f') {
    return 'https://res.cloudinary.com/dj1iwful4/image/upload/v1759167262/avatar_femenino.jpg';
  } else if (gender === 'male' || gender === 'Hombre' || gender === 'M' || gender === 'm') {
    return 'https://res.cloudinary.com/dj1iwful4/image/upload/v1756675311/avatar_masculino.jpg';
  }
  // Default fallback - try to use a generic default
  return 'https://res.cloudinary.com/dj1iwful4/image/upload/v1756675311/avatar_default.png';
};

// Memoized conversation item component for better performance
const ConversationItem = memo(({ conversation, onConversationSelect }) => {
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  const truncateMessage = (message, maxLength = 50) => {
    if (!message) return '';
    return message.length > maxLength 
      ? message.substring(0, maxLength) + '...' 
      : message;
  };

  return (
    <div
      key={conversation.id}
      className="flex items-center p-4 hover:bg-gray-50 rounded-lg transition-all duration-200 border border-transparent hover:border-pink-200 hover:shadow-md transform hover:scale-[1.02] active:scale-[0.98]"
    >
      {/* Foto de perfil clickeable */}
      <Link 
        to={`/perfil/${conversation.id}`}
        className="relative mr-4 hover:scale-110 transition-transform duration-200"
        title="Ver perfil completo"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={conversation.profile_picture || getDefaultAvatar(conversation.gender)}
          alt={conversation.first_name}
          className="w-12 h-12 rounded-full object-cover cursor-pointer"
          onError={(e) => {
            e.target.src = getDefaultAvatar(conversation.gender);
          }}
        />
        {conversation.is_online && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
        )}
      </Link>

      {/* Información de la conversación - clickeable para abrir chat */}
      <div 
        className="flex-1 min-w-0 cursor-pointer"
        onClick={() => onConversationSelect(conversation)}
      >
        <div className="flex justify-between items-start mb-1">
          <h3 className="font-semibold text-gray-900 truncate">
            {conversation.first_name} (@{conversation.username})
          </h3>
          <span className="text-xs text-gray-500 ml-2 flex-shrink-0">
            {formatTimeAgo(conversation.last_message_time)}
          </span>
        </div>
        <p className="text-sm text-gray-600 truncate">
          {conversation.last_sender_id === conversation.id ? '' : 'Tú: '}
          {truncateMessage(conversation.last_message)}
        </p>
      </div>

      {/* Botón Ver Perfil */}
      <Link
        to={`/perfil/${conversation.id}`}
        className="flex items-center space-x-1 px-3 py-2 text-pink-600 hover:text-pink-700 hover:bg-pink-50 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 mr-2"
        title="Ver perfil completo"
        onClick={(e) => e.stopPropagation()}
      >
        <Eye className="h-4 w-4" />
        <span className="text-sm font-medium">Perfil</span>
      </Link>

      {/* Contador de mensajes no leídos */}
      {conversation.unread_count > 0 && (
        <div className="ml-1 bg-pink-600 text-white text-xs rounded-full h-6 w-6 flex items-center justify-center font-semibold">
          {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
        </div>
      )}
    </div>
  );
});

ConversationItem.displayName = 'ConversationItem';

const ConversationList = ({ 
  conversations, 
  loading, 
  error, 
  onConversationSelect, 
  onRefresh,
  onPageChange,
  currentPage
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  
  // Memoize the conversations list to prevent unnecessary re-renders
  const memoizedConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    
    return conversations.filter(conversation => 
      conversation.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conversation.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (conversation.last_message && conversation.last_message.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [conversations, searchTerm]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Conversaciones</h2>
                     <button 
             onClick={onRefresh}
             className="text-pink-600 hover:text-pink-700 hover:scale-110 active:scale-95 transition-all duration-200 p-1 rounded-full hover:bg-pink-50"
             disabled={loading}
             title="Actualizar conversaciones"
           >
             🔄
           </button>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center p-4 animate-pulse">
              <div className="w-12 h-12 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full mr-4"></div>
              <div className="flex-1">
                <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
                <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-3/4"></div>
              </div>
              <div className="w-6 h-6 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Conversaciones</h2>
                     <button 
             onClick={onRefresh}
             className="text-pink-600 hover:text-pink-700 hover:scale-110 active:scale-95 transition-all duration-200 p-1 rounded-full hover:bg-pink-50"
             title="Actualizar conversaciones"
           >
             🔄
           </button>
        </div>
        <div className="text-center py-8">
          <p className="text-red-600 mb-4">{error}</p>
                     <button 
             onClick={onRefresh}
             className="bg-pink-600 text-white px-4 py-2 rounded hover:bg-pink-700 hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
           >
             Reintentar
           </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold">
            Conversaciones ({conversations.length})
          </h2>
        </div>
        <button 
          onClick={onRefresh}
          className="text-pink-600 hover:text-pink-700 hover:scale-110 active:scale-95 transition-all duration-200 p-1 rounded-full hover:bg-pink-50"
          title="Actualizar conversaciones"
        >
          🔄
        </button>
      </div>

      {/* Search functionality for conversation lists */}
      {conversations.length > 0 && (
        <div className="mb-4 relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Buscar conversaciones..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {searchTerm && (
            <p className="text-sm text-gray-500 mt-1">
              {memoizedConversations.length} conversación{memoizedConversations.length !== 1 ? 'es' : ''} encontrada{memoizedConversations.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      )}

      {memoizedConversations.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">💬</div>
          <h3 className="text-xl font-semibold mb-2">
            {searchTerm ? 'No se encontraron conversaciones' : 'No tienes conversaciones'}
          </h3>
          <p className="text-gray-600">
            {searchTerm 
              ? 'Intenta con otros términos de búsqueda'
              : 'Ve a "Buscar Pareja" para conocer personas y empezar a chatear'
            }
          </p>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-pink-600 hover:text-pink-700 hover:underline"
            >
              Limpiar búsqueda
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {memoizedConversations.map((conversation) => (
            <ConversationItem
              key={conversation.id}
              conversation={conversation}
              onConversationSelect={onConversationSelect}
            />
          ))}
        </div>
      )}

      {/* Pagination Controls */}
      {conversations.length === 20 && (
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              currentPage === 1 || loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105 active:scale-95'
            }`}
          >
            ← Anterior
          </button>
          
          <span className="text-gray-600 font-medium">
            Página {currentPage}
          </span>
          
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
              loading
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-pink-600 text-white hover:bg-pink-700 hover:scale-105 active:scale-95'
            }`}
          >
            Siguiente →
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(ConversationList);
